import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface ETSFIncident {
  id: string
  location: 'BHX' | 'LHR'
  incident_date: string
  incident_type: 'unauthorised_removal' | 'cctv_failure' | 'stock_discrepancy' | 'hmrc_visit' | 'access_breach' | 'unmanifested_goods' | 'other'
  description: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  reported_to_hmrc: boolean
  reported_to_hmrc_date: string | null
  hmrc_reference: string | null
  action_taken: string | null
  resolved: boolean
  resolved_date: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface IncidentFormData {
  location: 'BHX' | 'LHR'
  incident_date: string
  incident_type: 'unauthorised_removal' | 'cctv_failure' | 'stock_discrepancy' | 'hmrc_visit' | 'access_breach' | 'unmanifested_goods' | 'other'
  description: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  reported_to_hmrc: boolean
  reported_to_hmrc_date: string | null
  hmrc_reference: string | null
  action_taken: string | null
}

export function useETSFIncidents(location?: 'BHX' | 'LHR') {
  const [incidents, setIncidents] = useState<ETSFIncident[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadIncidents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('etsf_incidents')
        .select('*')
        .order('incident_date', { ascending: false })

      if (location) {
        query = query.eq('location', location)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr
      setIncidents((data || []) as ETSFIncident[])
    } catch (e: any) {
      setError(e.message || 'Failed to load incidents')
    } finally {
      setLoading(false)
    }
  }, [location])

  useEffect(() => {
    loadIncidents()
  }, [loadIncidents])

  const createIncident = useCallback(
    async (incidentData: IncidentFormData & { created_by: string }) => {
      const { data, error: qErr } = await supabase
        .from('etsf_incidents')
        .insert([incidentData])
        .select()
        .single()

      if (qErr) throw qErr
      await loadIncidents()
      return data as ETSFIncident
    },
    [loadIncidents]
  )

  const updateIncident = useCallback(
    async (id: string, updates: Partial<ETSFIncident>) => {
      const { data, error: qErr } = await supabase
        .from('etsf_incidents')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (qErr) throw qErr
      await loadIncidents()
      return data as ETSFIncident
    },
    [loadIncidents]
  )

  const markResolved = useCallback(
    async (id: string, actionTaken: string) => {
      const { data, error: qErr } = await supabase
        .from('etsf_incidents')
        .update({
          resolved: true,
          resolved_date: new Date().toISOString().split('T')[0],
          action_taken: actionTaken,
        })
        .eq('id', id)
        .select()
        .single()

      if (qErr) throw qErr
      await loadIncidents()
      return data as ETSFIncident
    },
    [loadIncidents]
  )

  const getStats = useCallback(() => {
    const openIncidents = incidents.filter(i => !i.resolved)
    const criticalIncidents = incidents.filter(i => !i.resolved && i.severity === 'critical')
    const highIncidents = incidents.filter(i => !i.resolved && i.severity === 'high')
    const thisMonth = incidents.filter(i => {
      const incidentMonth = new Date(i.incident_date).toISOString().substring(0, 7)
      const currentMonth = new Date().toISOString().substring(0, 7)
      return incidentMonth === currentMonth
    })
    const criticalThisMonth = thisMonth.filter(i => i.severity === 'critical')

    return {
      openCount: openIncidents.length,
      criticalCount: criticalIncidents.length,
      highCount: highIncidents.length,
      criticalThisMonth: criticalThisMonth.length,
    }
  }, [incidents])

  return {
    incidents,
    loading,
    error,
    reload: loadIncidents,
    createIncident,
    updateIncident,
    markResolved,
    getStats,
  }
}
